import { useEffect, useState, useMemo } from 'react';
import { getUnitMeasurement } from '../components/dashboard/documents/ViewNIR';
import { useSearchParams } from 'react-router-dom';
import useApiClient from '../hooks/useApiClient';

const PrintableGRN = () => {
    const [searchParams] = useSearchParams()
    const documentId = searchParams.get('id')
    const [grnData, setGrnData] = useState(null);
    const apiClient = useApiClient();

    useEffect(() => {
        const loadGrnData = async () => {
            try {
                const response = await apiClient.get(`/grn/${documentId}`);
                setGrnData(response);
            } catch (error) {
                console.error('Error fetching GRN data:', error);
                setGrnData(null);
            }
        }
        
        loadGrnData();
    }, [documentId, apiClient]);

    const data = useMemo(() => {
        if (!grnData || !grnData.items) return [];
        const vatRate = grnData.isVatPayer ? 0.21 : 0;
        return grnData.items.map((item, index) => {
            // Extract relevant values
            const pretFaraTVA = parseFloat(item.unitPrice) || 0;
            const pretCuTVA = pretFaraTVA * (1 + vatRate);
            const cantitateaTrimisa = parseFloat(item.quantitySent) || parseFloat(item.quantityReceived) || 0;
            const cantitateaReceptionata = parseFloat(item.quantityReceived) || 0;
            const valoareaIntrare = pretFaraTVA * cantitateaReceptionata;
            const pretUnitarVanzare = parseFloat(item.sellingPrice) || 0;
            const adaosComercialProc = parseFloat(item.markup) || 0;
            const valoareVanzareFaraTVA = (pretUnitarVanzare - pretFaraTVA) * cantitateaReceptionata;
            const valoareAdaosComercial = (cantitateaReceptionata * pretFaraTVA) + (cantitateaReceptionata * pretFaraTVA) * (adaosComercialProc / 100);

            return {
                nrCrt: index + 1,
                denumirea: item.productName,
                um: item.um,
                cantitateaTrimisa: cantitateaTrimisa,
                cantitateaReceptionata: cantitateaReceptionata,
                pretUnitarFaraTVA: pretFaraTVA.toFixed(2) + ' / ' + pretCuTVA.toFixed(2),
                valoareaIntrare: valoareaIntrare.toFixed(2),
                pretUnitarVanzare: pretUnitarVanzare.toFixed(2),
                adaosComercialProc: adaosComercialProc.toFixed(2),
                valoareVanzareFaraTVA: valoareVanzareFaraTVA.toFixed(2),
                valoareAdaosComercial: valoareAdaosComercial.toFixed(2),
            };
        });
    }, [grnData]);

    if (!grnData) {
        return (
            <div className="p-8 text-center">
                <h1 className="text-xl font-bold text-red-600 mb-4">Eroare la încărcarea datelor NIR-ului</h1>
                <p className="text-gray-700">Datele necesare pentru afișarea NIR-ului nu au putut fi încărcate.</p>
                <p className="text-gray-500 mt-2">Verificați consola pentru detalii sau încercați să generați din nou documentul.</p>
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return '_________';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    return (
        <div className="printable-container text-gray-800 font-sans text-xs">
            <style>
                {`
                    body {
                        background-color: #f0f0f0;
                    }
                    .printable-container {
                        width: 210mm;
                        margin: 20px auto;
                        padding: 15mm;
                        box-shadow: 0 0 10px rgba(0,0,0,0.1);
                        border: 1px solid #ccc;
                        background: white;
                        max-height: 90vh;
                        overflow-y: auto;
                        overflow-x: hidden;
                    }
                    .document-content {
                        width: 100%;
                    }
                    .page-footer {
                        width: 100%;
                        background: #f8f9fa;
                        border-top: 2px solid #2c3e50;
                        border-bottom: 2px solid #2c3e50;
                        padding: 10px 15px;
                        box-sizing: border-box;
                        margin-top: 30px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    @media print {
                        * {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                            color-adjust: exact !important;
                        }
                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            background-color: #fff !important;
                            width: 100% !important;
                            height: auto !important;
                            overflow: visible !important;
                            position: static !important;
                        }
                        a[href]:after {
                            content: none !important;
                        }
                        .printable-container {
                            width: 100%;
                            margin: 0;
                            padding: 10mm;
                            box-shadow: none;
                            border: none;
                            max-height: none;
                            overflow: visible;
                        }
                        .no-print {
                            display: none !important;
                        }
                        .page-footer {
                            padding: 10px;
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }
                        table {
                            page-break-inside: auto;
                        }
                        tbody tr {
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }
                        tfoot {
                            display: table-footer-group;
                        }
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                    }
                    th, td {
                        padding: 4px 6px;
                        text-align: left;
                    }
                    .header-main {
                        background-color: #2c3e50;
                        color: white;
                        padding: 15px;
                        margin-bottom: 20px;
                    }
                    .header-main h1 {
                        font-size: 1.8em;
                        font-weight: bold;
                        margin:0;
                    }
                    .header-main p {
                        font-size: 0.9em;
                        margin:0;
                    }
                    .details-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                        gap: 20px;
                        margin-bottom: 20px;
                    }
                    .details-box {
                        border: 1px solid #333;
                        padding: 12px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .details-box h3 {
                        font-weight: bold;
                        margin-bottom: 5px;
                        font-size: 0.9em;
                        text-transform: uppercase;
                    }
                    .items-table th {
                        border-bottom: 1px solid #555;
                        font-weight: bold;
                    }
                    .items-table td {
                        border-bottom: 1px dotted #ccc;
                    }
                    .items-table .text-right { text-align: right; }
                    .total-row td {
                        font-weight: bold;
                        border-top: 2px solid #333;
                        padding-top: 8px;
                    }
                    .items-table tbody tr {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                    .items-table tfoot {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                    .items-table tfoot tr {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                    .details-grid, .header-main {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                    .footer-notes {
                        margin-top: 20px;
                        font-size: 0.8em;
                        color: #555;
                    }
                    .section-title {
                        font-weight: bold;
                        text-transform: uppercase;
                        margin-top: 8px;
                        margin-bottom: 4px;
                        font-size: 0.9em;
                    }
                    .mb-4 {
                        margin-bottom: 16px;
                    }
                    .underline-input {
                         border-bottom: 1px dotted #333;
                         padding: 0 2px;
                         display: inline-block;
                         min-width: 80px;
                    }
                `}
            </style>

            <div className="no-print mb-6 flex gap-3">
                <button
                    onClick={() => window.print()}
                    className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                    Printează / Salvează PDF
                </button>
            </div>

            <div className="document-content">
                <div className="header-main">
                    <h1>NOTA RECEPTIE SI CONSTATARE DIFERENTE</h1>
                    <p className='py-2'>Data: {formatDate(grnData.date)}</p>
                    {grnData.seriesNumber && <p>Serie și număr: {grnData.seriesNumber}</p>}
                </div>

                    <div className="details-grid">
                        <div className="details-box">
                            <h3>RECEPTOR</h3>
                            <p><strong>{grnData.shop.name}</strong></p>
                            <p>Adresa: {grnData.shop.address || '-'}</p>
                        </div>
                        <div className="details-box">
                            <h3>FURNIZOR</h3>
                            <p><strong>{grnData.vendor?.name || 'N/A'}</strong></p>
                            <p>Factura nr.: {grnData.invoiceReceivedNr || 'N/A'}</p>
                        </div>
                    </div>

                    <div className="details-box mb-4">
                        <h3 className="section-title">Detalii Recepție</h3>
                        <div className="text-xs text-justify">
                            Membrii comisiei de recepție, am procedat la recepționarea valorilor materiale furnizate de: <span className="underline-input">{grnData.vendor?.name || '__________________'}</span>, cu factura nr.: <span className="underline-input">{grnData.invoiceReceivedNr || '_________'}</span>, și documente însoțitoare: <span className="underline-input text-transparent">_________</span>
                        </div>
                        <div className="text-xs mt-2">din <span className="underline-input">{formatDate(grnData.date)}</span></div>
                    </div>

                    <h3 className="section-title">Produse/Servicii</h3>
                    <table className="items-table">
                        <thead>
                            <tr>
                                <th rowSpan={2}>Nr.<br/>Crt.</th>
                                <th rowSpan={2}>Denumirea produselor sau a serviciilor</th>
                                <th rowSpan={2}>U.M.</th>
                                <th colSpan={2}>Cantitate</th>
                                <th colSpan={2}>Recepție</th>
                                {grnData.priceBasis === 'SELLING_PRICE' && (
                                    <>
                                        <th rowSpan={2}>Valoare adaos comercial</th>
                                        <th rowSpan={2}>Procent adaos comercial</th>
                                        <th rowSpan={2}>Valoare vânzare (cu TVA inclus)</th>
                                    </>
                                )}
                            </tr>
                            <tr>
                                <th>cnf doc</th>
                                <th>recepționat</th>
                                <th>{`Preț unitar ${grnData.isVatPayer ? '(fără TVA)' : '(fără TVA)'}`} </th>
                                <th>Valoare intrare</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, i) => (
                                <tr key={i}>
                                    <td>{row.nrCrt}</td>
                                    <td>{row.denumirea}</td>
                                    <td className="text-center">{getUnitMeasurement(row.um)}</td>
                                    <td className="text-right">{row.cantitateaTrimisa}</td>
                                    <td className="text-right">{row.cantitateaReceptionata}</td>
                                    <td className="text-right">{row.pretUnitarFaraTVA.split(' / ')[0]}</td>
                                    <td className="text-right">{row.valoareaIntrare}</td>
                                    {grnData.priceBasis === 'SELLING_PRICE' && (
                                        <>
                                            <td className="text-right">{row.valoareAdaosComercial}</td>
                                            <td className="text-right">{row.adaosComercialProc}%</td>
                                            <td className="text-right">{row.pretUnitarVanzare}</td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            {data.length === 0 && (
                                <tr><td colSpan={grnData.priceBasis === 'SELLING_PRICE' ? "10" : "7"} className="text-center py-4">Nu sunt produse disponibile.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="total-row">
                                <td colSpan={3} className="text-center">TOTAL</td>
                                <td className="text-right">{data.reduce((sum, row) => sum + parseFloat(row.cantitateaTrimisa), 0).toFixed(2)}</td>
                                <td className="text-right">{data.reduce((sum, row) => sum + parseFloat(row.cantitateaReceptionata), 0).toFixed(2)}</td>
                                <td></td>
                                <td className="text-right">{data.reduce((sum, row) => sum + parseFloat(row.valoareaIntrare), 0).toFixed(2)}</td>
                                {grnData.priceBasis === 'SELLING_PRICE' && (
                                    <>
                                        <td className="text-right">{data.reduce((sum, row) => sum + parseFloat(row.valoareAdaosComercial), 0).toFixed(2)}</td>
                                        <td></td>
                                        <td className="text-right">{data.reduce((sum, row) => sum + parseFloat(row.pretUnitarVanzare), 0).toFixed(2)}</td>
                                    </>
                                )}
                            </tr>
                        </tfoot>
                    </table>

                    {/* Notes section */}
                    {grnData.notes && (
                        <div className="details-box mb-4">
                            <h3 className="section-title">Observații</h3>
                            <div className="whitespace-pre-line">{grnData.notes}</div>
                        </div>
                    )}

                    {/* Footer text */}
                    <div className="footer-notes">
                        Membrii comisiei de recepție, am procedat la recepționarea valorilor materiale furnizate de {grnData.shop.name} {grnData?.invoiceNumber ? ', conform documentului ' + grnData.invoiceNumber : '.'}
                    </div>

                <div className="page-footer">
                    <div className="flex justify-between" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            <h3 style={{ fontWeight: 700, fontSize: '0.85em', color: '#2c3e50', marginBottom: 4 }}>Comisia de Recepție</h3>
                            <p style={{ margin: 0, color: 'rgb(98,98,98)' }}>Semnături: _________________________</p>
                        </div>
                        <div>
                            <h3 style={{ fontWeight: 700, fontSize: '0.85em', color: '#2c3e50', marginBottom: 4 }}>Reprezentant Furnizor</h3>
                            <p style={{ margin: 0, color: 'rgb(98,98,98)' }}>Semnătură: _________________________</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintableGRN;